import { useScreenClass, useIsScreenClassInitialized } from '@vp/swan'
import { useSelector } from 'react-redux'
import { booleanRenderPropertySelector, stringRenderPropertySelector } from '~/client/store/config'
import { RenderProperty } from 'shared/renderProperties'
import { useMemo } from 'react'
import { useTranslations } from '~/client/hooks/useTranslations'
import { PURPOSE_NAMES, TemplateField } from '../../Schemes/store/types'

interface PersonalizationConfig {
  isEnabled: boolean
  mode: 'modal' | 'sidebar'
  imageCopyKey: string
  templateFields: TemplateField[]
}

export const usePersonalizationConfig = (): PersonalizationConfig => {
  const screenClass = useScreenClass()
  const isScreenClassInitialized = useIsScreenClassInitialized()
  const localize = useTranslations()

  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
  const stringRenderPropSelector = useSelector(stringRenderPropertySelector)

  const isEnabled = booleanRenderProperty(RenderProperty.ShowPersonalizationUI)
  const imageCopyKey = stringRenderPropSelector(RenderProperty.PersonalizationImageCopy) || 'Photo'
  const templateTextFieldsString = stringRenderPropSelector(RenderProperty.PersonalizationTextFields) ?? ''

  const purposeNames = useMemo(
    () => templateTextFieldsString.split(',').filter((val): val is PURPOSE_NAMES => Object.values<string>(PURPOSE_NAMES).includes(val)),
    [templateTextFieldsString],
  )

  const templateFields = useMemo(() => {
    const purposePlaceholders = {
      [PURPOSE_NAMES.COMPANY_NAME]: localize('PersonalizationPlaceholderPurposeCompanyname'),
      [PURPOSE_NAMES.FAMILY_NAME]: localize('PersonalizationPlaceholderPurposeFamilyname'),
      [PURPOSE_NAMES.FULL_NAME]: localize('PersonalizationPlaceholderPurposeFullname'),
    }

    return purposeNames.map((purposeName) => ({
      purposeName,
      placeholder: purposePlaceholders[purposeName],
    }))
  }, [localize, purposeNames])

  const isSmallScreen = useMemo(() => {
    const smallScreenClasses = ['xs']

    return isScreenClassInitialized && smallScreenClasses.includes(screenClass)
  }, [screenClass, isScreenClassInitialized])

  return {
    isEnabled,
    mode: isSmallScreen ? 'modal' : 'sidebar',
    imageCopyKey,
    templateFields,
  }
}
